import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { ErrorMessage, Field, Loading, Notice, PageHeader } from '../components'
import { getSupportContactSettings, saveSupportContactSettings, type SupportContactSettings } from '../lib/supportContactApi'

function digitsOnly(value: string) {
  const digits = value.replace(/\D/g, '')
  return digits.startsWith('55') ? digits : digits ? `55${digits}` : ''
}

export function SupportContactPage() {
  const client = useQueryClient()
  const settings = useQuery({ queryKey: ['support-contact-settings'], queryFn: getSupportContactSettings })
  const [draft, setDraft] = useState<SupportContactSettings | null>(null)
  const [notice, setNotice] = useState('')
  const current = draft ?? settings.data
  const number = current?.whatsapp_number ?? ''
  const numberDigits = digitsOnly(number)
  const previewUrl = current?.whatsapp_enabled && numberDigits.length >= 12
    ? `https://wa.me/${numberDigits}${current.whatsapp_prefill_default ? `?text=${encodeURIComponent(current.whatsapp_prefill_default)}` : ''}`
    : null

  const save = useMutation({
    mutationFn: saveSupportContactSettings,
    onSuccess: async result => {
      setDraft(result)
      setNotice('Canais de suporte salvos.')
      await client.setQueryData(['support-contact-settings'], result)
    },
    onError: () => setNotice(''),
  })

  function update(patch: Partial<SupportContactSettings>) {
    if (current) setDraft({ ...current, ...patch })
  }

  if (settings.isPending) return <Loading />
  if (settings.error || !current) return <section><PageHeader title="WhatsApp / contato" description="Configure o canal de WhatsApp exibido nos pontos públicos de suporte." /><ErrorMessage error={settings.error} /></section>

  return <section>
    <PageHeader title="WhatsApp / contato" description="Configure o canal de WhatsApp exibido no site e na ajuda." />
    {notice && <Notice>{notice}</Notice>}
    <form className="panel form-panel" onSubmit={event => {
      event.preventDefault()
      setNotice('')
      save.mutate({
        ...current,
        whatsapp_number: numberDigits || null,
      })
    }}>
      <Field label="Número do WhatsApp" hint="Só dígitos com DDI. Ex.: 5524981751061. Vazio ou desligado esconde o botão no site.">
        <input aria-label="Número do WhatsApp" type="tel" inputMode="numeric" autoComplete="tel" maxLength={15} placeholder="5524981751061" value={numberDigits} disabled={save.isPending} onChange={event => update({ whatsapp_number: digitsOnly(event.target.value) })} />
      </Field>
      <label className="support-contact-toggle"><input type="checkbox" checked={current.whatsapp_enabled} disabled={save.isPending} onChange={event => update({ whatsapp_enabled: event.target.checked })} /> Exibir WhatsApp no site e na ajuda</label>
      <Field label="Mensagem padrão (opcional)">
        <textarea rows={3} maxLength={500} value={current.whatsapp_prefill_default} disabled={save.isPending} onChange={event => update({ whatsapp_prefill_default: event.target.value })} />
      </Field>
      <div className="support-contact-preview" aria-live="polite">
        <strong>Prévia do link</strong>
        {previewUrl ? <a href={previewUrl} target="_blank" rel="noreferrer">{previewUrl}</a> : <span>O botão ficará oculto enquanto o canal estiver desligado ou sem número válido.</span>}
      </div>
      {save.error && <ErrorMessage error={save.error} />}
      <button className="button button-primary" disabled={save.isPending}>{save.isPending ? 'Salvando…' : 'Salvar canais'}</button>
    </form>
  </section>
}
